import { useState, useMemo, useDeferredValue } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/StatusBadge.js';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { useLinkTypes, useIssuePicker, useCreateIssueLink, useDeleteIssueLink } from '@/hooks/useIssueLinks.js';
import { groupLinksByRelation, buildRelationOptions } from '@/lib/issue-links.js';
import type { JiraIssueLink, JiraIssuePickerResult } from '@shared/types.js';

interface LinkedIssuesProps {
  issueKey: string;
  links: JiraIssueLink[];
}

/** Jira's "Linked work items" panel: grouped by relation, with inline add/unlink. */
export function LinkedIssues({ issueKey, links }: LinkedIssuesProps) {
  const [adding, setAdding] = useState(false);
  const groups = useMemo(() => groupLinksByRelation(links), [links]);
  const deleteLink = useDeleteIssueLink(issueKey);

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h3 className="text-sm font-semibold text-gray-800">
          Linked work items
          {links.length > 0 && (
            <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{links.length}</span>
          )}
        </h3>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="-mr-1 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            title="Link work item"
            aria-label="Link work item"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
              <path d="M8 3v10M3 8h10" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {adding && <AddLinkForm issueKey={issueKey} onDone={() => setAdding(false)} />}

      {groups.length === 0 && !adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full px-4 py-3 text-left text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600"
        >
          Add linked work item
        </button>
      )}

      {groups.map((group) => (
        <div key={group.relation}>
          <div className={`px-4 pt-2.5 pb-1 text-xs font-semibold ${group.relation === 'is blocked by' ? 'text-red-700' : 'text-gray-500'}`}>
            {group.relation}
          </div>
          <ul className="pb-1.5">
            {group.links.map((link) => (
              <LinkRow
                key={link.id}
                link={link}
                removing={deleteLink.isPending && deleteLink.variables?.linkId === link.id}
                onRemove={() => deleteLink.mutate({ linkId: link.id, targetKey: link.issue.key })}
              />
            ))}
          </ul>
        </div>
      ))}

      {deleteLink.isError && (
        <p className="px-4 pb-2 text-xs text-red-600">Couldn&apos;t remove the link: {deleteLink.error.message}</p>
      )}
    </div>
  );
}

function LinkRow({ link, removing, onRemove }: { link: JiraIssueLink; removing: boolean; onRemove: () => void }) {
  const isDone = link.issue.status.category === 'done';
  return (
    <li className={`group relative px-4 py-1.5 hover:bg-gray-50 ${removing ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-1.5">
        {link.issue.issuetype && <IssueTypeIcon name={link.issue.issuetype.name} />}
        <Link
          to={`/issues/${link.issue.key}`}
          className={`shrink-0 text-xs font-semibold text-blue-600 hover:underline ${isDone ? 'text-gray-500 line-through' : ''}`}
        >
          {link.issue.key}
        </Link>
        <span className="flex-1" />
        {link.issue.priority && <PriorityIcon name={link.issue.priority.name} iconUrl={link.issue.priority.iconUrl} />}
        <StatusBadge status={link.issue.status} />
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          className="-mr-1 rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-200 hover:text-gray-700 focus:opacity-100 group-hover:opacity-100"
          title="Unlink work item"
          aria-label={`Unlink ${link.issue.key}`}
        >
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
            <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <Link
        to={`/issues/${link.issue.key}`}
        className={`mt-0.5 block text-sm leading-snug line-clamp-2 hover:text-blue-700 ${isDone ? 'text-gray-500' : 'text-gray-800'}`}
        title={link.issue.summary}
      >
        {link.issue.summary}
      </Link>
    </li>
  );
}

function AddLinkForm({ issueKey, onDone }: { issueKey: string; onDone: () => void }) {
  const { data: types, isLoading: typesLoading } = useLinkTypes();
  const options = useMemo(() => buildRelationOptions(types ?? []), [types]);
  const [relationValue, setRelationValue] = useState<string>('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<JiraIssuePickerResult | null>(null);
  const deferredQuery = useDeferredValue(query);
  const picker = useIssuePicker(selected ? '' : deferredQuery, issueKey);
  const createLink = useCreateIssueLink(issueKey);

  const relation = options.find((o) => o.value === relationValue) ?? options[0];
  const typedKey = query.trim().toUpperCase();
  const directKey = /^[A-Z][A-Z0-9_]*-\d+$/.test(typedKey) && typedKey !== issueKey ? typedKey : null;
  const targetKey = selected?.key ?? directKey;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!relation || !targetKey) return;
    createLink.mutate(
      { typeName: relation.typeName, direction: relation.direction, targetKey, relation: relation.label },
      { onSuccess: onDone },
    );
  }

  const results = (picker.data ?? []).filter((r) => r.key !== issueKey);
  // A full key that the search has nothing for can still be linked; don't cover the Link button
  const showResults = !selected && query.trim().length >= 2 && !(directKey && results.length === 0);

  return (
    <form onSubmit={handleSubmit} className="border-b bg-gray-50 p-3">
      <div className="flex flex-col gap-2">
        <select
          value={relation?.value ?? ''}
          onChange={(e) => setRelationValue(e.target.value)}
          disabled={typesLoading}
          className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
          aria-label="Link type"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="relative flex-1">
          {selected ? (
            <div className="flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-2 py-1.5 text-sm">
              <span className="font-medium text-blue-700">{selected.key}</span>
              <span className="min-w-0 flex-1 truncate text-gray-700">{selected.summary}</span>
              <button
                type="button"
                onClick={() => { setSelected(null); setQuery(''); }}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Clear selected work item"
              >
                &times;
              </button>
            </div>
          ) : (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by key or summary"
              className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
              aria-label="Work item to link"
              autoFocus
            />
          )}
          {showResults && (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-white py-1 shadow-lg">
              {picker.isFetching && results.length === 0 && (
                <li className="px-3 py-2 text-xs text-gray-400">Searching...</li>
              )}
              {!picker.isFetching && results.length === 0 && (
                <li className="px-3 py-2 text-xs text-gray-400">No matching work items</li>
              )}
              {results.map((r) => (
                <li key={r.key}>
                  <button
                    type="button"
                    onClick={() => setSelected(r)}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                  >
                    <span className="shrink-0 font-medium text-blue-600">{r.key}</span>
                    <span className="truncate text-gray-700">{r.summary}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {createLink.isError && (
        <p className="mt-2 text-xs text-red-600">Couldn&apos;t create the link: {createLink.error.message}</p>
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!relation || !targetKey || createLink.isPending}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {createLink.isPending ? 'Linking...' : 'Link'}
        </button>
      </div>
    </form>
  );
}
